/**
 * Module summary:
 * This component renders the full assessment workflow. It loads the assessment
 * definition, tracks answers and photos, groups questions into sections,
 * supports search and subsection navigation, and coordinates save/submit
 * actions for the parent page.
 */

import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Question } from '../../../types/assessment';
import { QuestionPhotoMap } from '../../../types/assessment';
import { useAssessment, useAssessmentResponse } from '../../../hooks/useAssessment';
import { QuestionCard } from '../../molecules/QuestionCard';
import { Button } from '../../atoms/Button';
import { Card } from '../../atoms/Card';
import './AssessmentForm.css';

interface AssessmentFormProps {
assessmentId: string;
buildingId: string;
onSubmit?: (responses: Record<string, any>, photos: QuestionPhotoMap) => void;
onSave?: (responses: Record<string, any>, photos: QuestionPhotoMap) => Promise<void> | void;
initialData?: Record<string, any>;
initialPhotos?: QuestionPhotoMap;
}

type GroupedQuestions = Record<string, Question[]>;

const cleanSectionLabel = (value: string): string =>
  value
    // Remove numbering and trailing parenthetical notes so section labels stay short in the UI.
    .replace(/^\s*\d+\.\s*/, '')
    .replace(/\s*\([^)]*\)\s*$/u, '')
    .trim();

export const AssessmentForm = ({
assessmentId,
onSubmit,
onSave,
initialData,
initialPhotos
}: AssessmentFormProps): JSX.Element => {
  const { assessment, loading, error } = useAssessment(assessmentId);
  const { responses, updateResponse, updateComment, getResponse, getComment, getPhotos, updatePhotos, photos } =
    useAssessmentResponse(initialData, initialPhotos);

  const [activeCategory, setActiveCategory] = useState<number>(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [query, setQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [selectedSubsection, setSelectedSubsection] = useState('All');
  const [showTopBtn, setShowTopBtn] = useState(false);
  const [showMobileSections, setShowMobileSections] = useState(false);
  const chipsRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const onScroll = () => setShowTopBtn(window.scrollY > 400);
    // Watch scroll position so the back-to-top button appears only after the form becomes long enough to need it.
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleNext = useCallback(() => {
    if (!assessment) return;
    if (activeCategory < assessment.categories.length - 1) {
      // Advance to the next category and reset subsection/search-specific transient UI state.
      setActiveCategory((prev) => prev + 1);
      setSelectedSubsection('All');
      setValidationErrors({});
      setShowMobileSections(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [assessment, activeCategory]);

  const handlePrevious = useCallback(() => {
    if (activeCategory > 0) {
      // Move back one category and clear per-section UI state so the previous section opens cleanly.
      setActiveCategory((prev) => prev - 1);
      setSelectedSubsection('All');
      setValidationErrors({});
      setShowMobileSections(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeCategory]);

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    setIsSaving(true);
    setSaveMessage('');
    try {
      // Delegate the persistence step to the parent so this component stays focused on form behavior.
      await onSave(responses, photos);
      setSaveMessage('Progress saved.');
      window.setTimeout(() => setSaveMessage(''), 2500);
    } catch (err) {
      setSaveMessage(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  }, [onSave, responses, photos]);

  const handleSubmit = useCallback(async () => {
    if (!assessment) return;

    const nextErrors: Record<string, string> = {};
    assessment.categories.forEach((category) => {
      category.questions.forEach((question) => {
        const value = responses[question.id];
        const isEmpty =
          value === '' ||
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0);
        if (isEmpty) {
          // Allow partially completed assessments to be submitted without forcing every unanswered question.
          return;
        }
        if (typeof value === 'string' && question.validation?.minLength && value.trim().length < question.validation.minLength) {
          nextErrors[question.id] = question.validation.customMessage || `Please enter at least ${question.validation.minLength} characters.`;
          return;
        }
        if (typeof value === 'string' && question.validation?.maxLength && value.length > question.validation.maxLength) {
          nextErrors[question.id] = question.validation.customMessage || `Please keep the response under ${question.validation.maxLength} characters.`;
        }
      });
    });

    if (Object.keys(nextErrors).length > 0) {
      setValidationErrors(nextErrors);
      return;
    }

    setValidationErrors({});
    setIsSubmitting(true);
    try {
      await onSubmit?.(responses, photos);
    } finally {
      setIsSubmitting(false);
    }
  }, [assessment, onSubmit, responses, photos]);

  const categoryProgress = useMemo(() => {
    if (!assessment) return [];

    return assessment.categories.map((category, idx) => {
      const total = category.questions.length;
      const answered = category.questions.filter((question) => {
        // Count each answered question so the sidebar can display section-level completion.
        const value = getResponse(question.id);
        return value !== undefined && value !== null && value !== '';
      }).length;

      return {
        idx,
        id: category.id,
        title: cleanSectionLabel(category.title),
        total,
        answered,
        percent: total ? Math.round((answered / total) * 100) : 0
      };
    });
  }, [assessment, getResponse]);

  if (loading) {
    return (
      <div className="assessment-form assessment-form--loading">
        <div className="assessment-form__spinner" />
        <p>Loading assessment...</p>
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="assessment-form assessment-form--error">
        <h3>Error Loading Assessment</h3>
        <p>{error || 'Assessment not found'}</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  const currentCategory = assessment.categories[activeCategory];
  if (!currentCategory) return <></>;

  const isSearching = activeQuery.trim().length > 0;

  const questionMatchesSearch = (question: Question): boolean => {
    if (!activeQuery.trim()) return true;
    const needle = activeQuery.toLowerCase();
    // Match against both visible question text and ids so internal identifiers are searchable too.
    return question.text.toLowerCase().includes(needle) || question.id.toLowerCase().includes(needle);
  };

  const categoriesToShow = isSearching ? assessment.categories : [currentCategory];
  const allVisibleGroups: { categoryTitle: string; name: string; questions: Question[] }[] = [];

  categoriesToShow.forEach((category) => {
    const grouped: GroupedQuestions = category.questions.reduce((acc, question) => {
      const name = question.helpText?.replace('Section: ', '').trim() || 'General';
      // Bucket questions by subsection label so the UI can render smaller grouped blocks inside a category.
      if (!acc[name]) acc[name] = [];
      acc[name].push(question);
      return acc;
    }, {} as GroupedQuestions);

    Object.keys(grouped)
      .filter((name) => isSearching || selectedSubsection === 'All' || name === selectedSubsection)
      .forEach((name) => {
        const questions = grouped[name].filter(questionMatchesSearch);
        if (questions.length > 0) {
          allVisibleGroups.push({ categoryTitle: category.title, name, questions });
        }
      });
  });

  const groupedQuestions: GroupedQuestions = currentCategory.questions.reduce((acc, question) => {
    const name = question.helpText?.replace('Section: ', '').trim() || 'General';
    if (!acc[name]) acc[name] = [];
    acc[name].push(question);
    return acc;
  }, {} as GroupedQuestions);

  const subsectionNames = Object.keys(groupedQuestions);
  const selectedSubsectionIndex = subsectionNames.indexOf(selectedSubsection);
  const canGoNextSubsection = !isSearching && subsectionNames.length > 0 && selectedSubsectionIndex < subsectionNames.length - 1;
  const nextSubsectionLabel = selectedSubsection === 'All'
    ? subsectionNames[0]
    : subsectionNames[selectedSubsectionIndex + 1];

  const handleSearch = () => {
    // Commit the current search box value so typing and filtering can be controlled separately.
    setActiveQuery(query);
  };

  const clearSearch = () => {
    // Clear both the draft text and the active filter, then return focus to the search input for faster repeat searches.
    setQuery('');
    setActiveQuery('');
    searchInputRef.current?.focus();
  };

  const handleNextSubsection = () => {
    if (!canGoNextSubsection || !nextSubsectionLabel) return;
    setSelectedSubsection(nextSubsectionLabel);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalQuestions = assessment.categories.reduce((sum, category) => sum + category.questions.length, 0);
  const answeredCount = assessment.categories
    .flatMap((category) => category.questions)
    .filter((question) => {
      // Recount answered questions across the whole assessment so the global progress bar stays accurate.
      const value = getResponse(question.id);
      return value !== undefined && value !== null && value !== '';
    }).length;
  const progress = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const incompleteSections = categoryProgress.filter((section) => section.percent < 100);
  const currentSectionProgress = categoryProgress[activeCategory];
  const currentSectionLabel = cleanSectionLabel(currentCategory.title);
  const currentSectionAnswered = currentSectionProgress?.answered ?? 0;
  const currentSectionTotal = currentSectionProgress?.total ?? currentCategory.questions.length;

  const scrollChips = (left: number) => {
    // Move the subsection chip rail horizontally to reveal hidden subsection buttons on smaller screens.
    chipsRef.current?.scrollBy({ left, behavior: 'smooth' });
  };

  return (
    <div className="assessment-form-layout">
      <aside className={`assessment-sidebar ${showMobileSections ? 'assessment-sidebar--open' : ''}`}>
        <Card padding="md" className="assessment-sidebar__card">
          <h3>Report Progress</h3>

          <div className="progress-bar">
            <div className="progress-bar__fill" style={{ width: `${progress}%` }} />
          </div>
          <p className="progress-bar__text">
            {answeredCount}/{totalQuestions} answered ({progress}%)
          </p>

          <div className="sidebar-section">
            <h4>Sections</h4>
            <div className="section-list">
              {categoryProgress.map((section) => (
                <button
                  key={section.id}
                  className={`section-item ${section.idx === activeCategory ? 'section-item--active' : ''}`}
                  onClick={() => {
                    setActiveCategory(section.idx);
                    setSelectedSubsection('All');
                    setShowMobileSections(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <span className="dot" />
                  <span>{section.title}</span>
                  <span className="pill">{section.percent}%</span>
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <h4>Sections Incomplete</h4>
            <ul className="missing-list">
              {incompleteSections.slice(0, 8).map((section) => (
                <li key={section.id}>
                  <button
                    onClick={() => {
                      setActiveCategory(section.idx);
                      setSelectedSubsection('All');
                      setShowMobileSections(false);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    {section.title}: {section.answered}/{section.total} answered ({section.percent}%)
                  </button>
                </li>
              ))}
              {incompleteSections.length === 0 && <li>All sections complete.</li>}
            </ul>
          </div>
        </Card>
      </aside>

      <div className="assessment-form">
        <Card className="assessment-form__header" padding="lg">
          <div className="assessment-form__hero">
            <h1 className="assessment-form__title">Security Assessment</h1>
            <p className="assessment-form__description">{assessment.metadata.description}</p>
          </div>

          <div className="assessment-form__sticky-controls">
            <div className="assessment-status-strip">
              <div className="assessment-status-strip__item">
                <span className="assessment-status-strip__label">Current section</span>
                <strong className="assessment-status-strip__value">{currentSectionLabel}</strong>
              </div>
              <div className="assessment-status-strip__item">
                <span className="assessment-status-strip__label">Section progress</span>
                <strong className="assessment-status-strip__value">{currentSectionAnswered}/{currentSectionTotal} answered</strong>
              </div>
              <div className="assessment-status-strip__item">
                <span className="assessment-status-strip__label">Overall progress</span>
                <strong className="assessment-status-strip__value">{progress}% complete</strong>
              </div>
            </div>

            <div className="assessment-mobile-nav">
              <button
                type="button"
                className="assessment-mobile-nav__toggle"
                onClick={() => setShowMobileSections((prev) => !prev)}
              >
                {showMobileSections ? 'Hide Sections' : 'Browse Sections'}
              </button>
              {currentSectionProgress && (
                <div className="assessment-mobile-nav__status">
                  <span>{currentSectionProgress.title}</span>
                  <span>{currentSectionProgress.percent}% complete</span>
                </div>
              )}
            </div>

            <div className="assessment-toolbar">
              <div className="assessment-toolbar__save">
                <Button variant="ghost" onClick={() => void handleSave()} loading={isSaving}>
                  Save Progress
                </Button>
                {saveMessage && <p className="assessment-toolbar__save-note">{saveMessage}</p>}
              </div>

              <label className="toolbar-field">
                <span>Jump to section</span>
                <select
                  value={activeCategory}
                  onChange={(e) => {
                    setActiveCategory(Number(e.target.value));
                    setSelectedSubsection('All');
                    setShowMobileSections(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  {assessment.categories.map((category, index) => (
                    <option key={category.id} value={index}>
                      {cleanSectionLabel(category.title)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="toolbar-field toolbar-field--grow">
                <span>Search questions</span>
                <div className="toolbar-search">
                  <input
                    ref={searchInputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    placeholder="Type keyword (ex: keypad, backup, camera)"
                  />
                  {activeQuery && (
                    <button type="button" className="toolbar-search__clear" onClick={clearSearch} aria-label="Clear search">
                      {'\u00D7'}
                    </button>
                  )}
                  <button type="button" className="toolbar-search__btn" onClick={handleSearch}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                    Search
                  </button>
                </div>
              </label>
            </div>

            {subsectionNames.length > 0 && (
              <div className="subsection-nav">
                <button type="button" className="subsection-nav__arrow" onClick={() => scrollChips(-220)}>
                  {'\u2039'}
                </button>

                <div className="subsection-chips" aria-label="Subsections" ref={chipsRef}>
                  <button
                    type="button"
                    className={`chip ${selectedSubsection === 'All' ? 'chip--active' : ''}`}
                    onClick={(event) => {
                      setSelectedSubsection('All');
                      event.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    }}
                  >
                    All
                  </button>

                  {subsectionNames.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className={`chip ${selectedSubsection === name ? 'chip--active' : ''}`}
                      onClick={(event) => {
                        setSelectedSubsection(name);
                        event.currentTarget.scrollIntoView({
                          behavior: 'smooth',
                          inline: 'center',
                          block: 'nearest'
                        });
                      }}
                    >
                      {cleanSectionLabel(name)}
                    </button>
                  ))}
                </div>

                <button type="button" className="subsection-nav__arrow" onClick={() => scrollChips(220)}>
                  {'\u203A'}
                </button>
              </div>
            )}
          </div>
        </Card>

        <Card className="assessment-form__category" padding="lg">
          <div className="category__header">
            <h2 className="category__title">{isSearching ? `Search results for "${activeQuery}"` : cleanSectionLabel(currentCategory.title)}</h2>
            {!isSearching && currentCategory.description && (
              <p className="category__description">{currentCategory.description}</p>
            )}
            {isSearching && (
              <p className="category__description">
                {allVisibleGroups.reduce((sum, group) => sum + group.questions.length, 0)} matching questions across all sections
              </p>
            )}
          </div>

          <div className="category__questions">
            {allVisibleGroups.length === 0 && <p>No questions match your filters{isSearching ? ' across any section' : ' in this section'}.</p>}

            {allVisibleGroups.map((group, groupIdx) => (
              <section key={`${group.categoryTitle}-${group.name}-${groupIdx}`} className="subsection-group">
                <div className="subsection-group__header">
                  <h3>{isSearching ? `${cleanSectionLabel(group.categoryTitle)} > ${cleanSectionLabel(group.name)}` : cleanSectionLabel(group.name)}</h3>
                  <span>{group.questions.length} questions</span>
                </div>

                <div className="subsection-group__body">
                  {group.questions.map((question: Question) => (
                    <div key={question.id} id={`q-${question.id}`}>
                      <QuestionCard
                        question={question}
                        value={getResponse(question.id)}
                        comment={getComment(question.id)}
                        onChange={(value) => updateResponse(question.id, value)}
                        onCommentChange={(comment) => updateComment(question.id, comment)}
                        error={validationErrors[question.id]}
                        searchQuery={activeQuery}
                        photos={getPhotos(question.id)}
                        onPhotosChange={(questionPhotos) => updatePhotos(question.id, questionPhotos)}
                      />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </Card>

        <div className="assessment-form__actions">
          <Button variant="ghost" onClick={handlePrevious} disabled={activeCategory === 0}>
            Previous
          </Button>

          <div className="assessment-form__actions-center">
            <Button
              variant="ghost"
              onClick={handleNextSubsection}
              disabled={!canGoNextSubsection}
              title={canGoNextSubsection ? 'Go to the next subsection' : 'You are at the last subsection'}
            >
              Next Subsection {'>'}
            </Button>
          </div>

          <div className="assessment-form__actions-right">
            <Button
              variant="secondary"
              onClick={handleNext}
              disabled={activeCategory >= assessment.categories.length - 1}
              title={activeCategory >= assessment.categories.length - 1 ? 'You are at the last category' : 'Go to the next category'}
            >
              Next Category {'>'}
            </Button>
            <Button onClick={handleSubmit} loading={isSubmitting}>
              Submit Assessment
            </Button>
          </div>
        </div>
      </div>

      {showTopBtn && (
        <button
          type="button"
          className="back-to-top"
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          {'\u2191'}
        </button>
      )}
    </div>
  );
};

export default AssessmentForm;
